import { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { TableData } from '../../../core/data/interface';
import PredefinedDateRanges from '../../../core/common/datePicker';
import { attendance } from '../../../core/common/selectoption/selectoption';
import Table from '../../../core/common/dataTable/index';
import { fetchReportApi } from '../../../core/data/services/reportService';
import { useCallback } from "react";
import { fetchReportsSuccess } from "../../../core/data/redux/reportSlice";
import { useDispatch } from "react-redux";
import ErrorModal from '../../../core/common/ErrorModal';
import SuccessModal from '../../../core/common/successModal';
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { getUserDetails } from "../../../lib/getUserDetails";
import { exportToExcel, exportToPDF } from '../../../utils/exportUtils';
import TooltipOption from "../../../core/common/tooltipOption";
import CommonSelect, { Option } from "../../../core/common/commonSelect";

const MealReport = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const [isLoadingReports, setIsLoadingReports] = useState(false);;
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');

  // Class and Section selection states
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState<number | null>(null);
  const [selectedAttendance, setSelectedAttendance] = useState<string | null>(null);

  const reports = useSelector((state: RootState) => state.reports.reports);


  // Filtered reports based on selected class, section, and attendance
  const filteredReports = useMemo(() => {
    if (!reports || reports.length === 0) return [];

    let filtered = reports;

    // Filter by class if selected
    if (selectedClass) {
      filtered = filtered.filter((report: any) => report.className === selectedClass);
    }

    // Filter by section if selected
    if (selectedClassSectionId) {
      filtered = filtered.filter((report: any) => parseInt(report?.classSectionId) === selectedClassSectionId);
    }

    // Filter by attendance if selected
    if (selectedAttendance) {
      filtered = filtered.filter((report: any) => {
        const present = (report as any).present || 0;
        const absent = (report as any).absent || 0;
        const onDuty = (report as any).onDuty || 0;

        switch (selectedAttendance) {
          case 'Present':
            return present > 0;
          case 'Absent':
            return absent > 0;
          case 'On Duty':
            return onDuty > 0;
          default:
            return true;
        }
      });
    }

    // If no filters are applied, show all data
    return filtered;
  }, [reports, selectedClass, selectedClassSectionId, selectedAttendance]);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const fetchReportsRef = useRef<typeof fetchReports>();

  // Get user role
  const getUserRole = useCallback(() => {
    try {
      const userData = getUserDetails();
      if (!userData || userData.length === 0) return null;
      return userData[0].role;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }, []);

  const userRole = getUserRole();
   const getDashboardRoute = (roleRaw: string) => {
      const role = roleRaw?.toLowerCase()?.trim();
  
      const direct: Record<string, keyof typeof routes> = {
        teacher: "teacherDashboard",
        headmaster: "HeadmasterDashboard",
        mealsupervisor:"mealSupervisor"
  
      };
  
      if (role in direct) return routes[direct[role]];
  
      if (role?.includes("teacher")) return routes.teacherDashboard;
      if (role?.includes("headmaster") || role?.includes("hm")) return routes.HeadmasterDashboard;
      if (role?.includes("meal supervisor") || role?.includes("Meal Supervisor")) return routes.mealSupervisor;
      const fallback: keyof typeof routes = "teacherDashboard";
      return routes[fallback] ?? "/";
    };
  
  
    const dashboardLink = useMemo(() => getDashboardRoute(userRole), [userRole]);
  

  // Group sections by class using user data (only for Teachers)
  const groupedClassSections = useMemo(() => {
    if (userRole !== 'Teacher') return {};

    try {
      const userData = getUserDetails();
      if (!userData || userData.length === 0) return {};

      const userInfo = userData[0];
      const schoolClasses = userInfo.relatedEntity.schoolClasses || [];

      const sections = schoolClasses.reduce((acc: any[], schoolClassObj: any) => {
        if (Array.isArray(schoolClassObj.classSection)) {
          const sections = schoolClassObj.classSection.map((section: any) => ({
            className: schoolClassObj?.schoolClass?.name || schoolClassObj?.name,
            sectionName: section?.name,
            classSectionId: section?.id,
          }));
          return [...acc, ...sections];
        }
        return acc;
      }, []);

      return sections.reduce((grouped: any, section: any) => {
        const { className } = section;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className].push(section);
        return grouped;
      }, {});
    } catch (error) {
      console.error('Error extracting class sections:', error);
      return {};
    }
  }, [userRole]);

  // Get unique class names for dropdown
  const classNames = useMemo(() => Object.keys(groupedClassSections), [groupedClassSections]);
  const classOptions: Option[] = classNames.map((c) => ({ value: c, label: c }));
  const currentOption = selectedClass ? classOptions.find(o => o.value === String(selectedClass)) ?? null : null;

  // Get sections for selected class
  const sectionsForSelectedClass = useMemo(() => {
    if (!selectedClass || !groupedClassSections[selectedClass]) return [];
    return groupedClassSections[selectedClass];
  }, [selectedClass, groupedClassSections]);

  const sectionOptions: Option[] = useMemo(
    () =>
      sectionsForSelectedClass.map((s: any) => ({
        value: String(s.classSectionId),
        label: s.sectionName,
      })),
    [sectionsForSelectedClass]
  );

  const currentSectionOption = useMemo(
    () =>
      selectedClassSectionId != null
        ? sectionOptions.find(o => o.value === String(selectedClassSectionId)) ?? null
        : null,
    [selectedClassSectionId, sectionOptions]
  );
  // If attendance is already { value, label }[] you can reuse it directly:
  const attendanceOptions: Option[] = attendance as Option[];

  // Compute the current option from selectedAttendance (string | null)
  const currentAttendanceOption =
    selectedAttendance != null
      ? attendanceOptions.find(o => String(o.value) === String(selectedAttendance)) ?? null
      : null;

  const handleApplyClick = () => {
    // Close the dropdown - filtering is done locally
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  // Fetch reports with date range
  const fetchReports = useCallback(async (startDate: string, endDate: string) => {
    setIsLoadingReports(true);
    try {
      const payload: any = {
        startDate,
        endDate,
        // isCumulative: false
      };

      // Get classSectionIds from user data directly to avoid dependency issues
      const userData = getUserDetails();
      const userSchoolClasses = userData?.[0]?.relatedEntity?.schoolClasses || [];
      const userClassSectionIds = (userSchoolClasses as any[])?.flatMap((item: any) =>
        item?.classSection?.map((section: any) => section?.id) || []
      ) || [];

      if (userClassSectionIds?.length > 0) {
        payload.classSectionIds = userClassSectionIds?.map((id) => parseInt(id, 10));
      }

      const data = await fetchReportApi(payload);
      console.log("report data Is :", data);
      // Handle different data structures from the API
      let reportsData: any[] = [];
      if (Array.isArray(data)) {
        reportsData = data;
      } else if (data && typeof data === 'object') {
        reportsData = (data as any).studentscount || [];
      }
      dispatch(fetchReportsSuccess(reportsData));
    } catch (error: any) {
      // Handle empty results gracefully
      dispatch(fetchReportsSuccess([]));

      // Only show error for actual API failures, not empty results
      if (error.message !== 'Student not found' && !error.message.includes('not found')) {
        const msg = error?.message ?? "Failed to fetch reports.";
        setError(msg);
        setShowErrorModal(true);
        console.error("Error fetching reports:", msg);
      }
    } finally {
      setIsLoadingReports(false);
    }
  }, [dispatch]);

  // Store fetchReports in ref to avoid dependency issues
  fetchReportsRef.current = fetchReports;

  // Handle date range change from PredefinedDateRanges
  const handleDateRangeChange = useCallback((startDate: string, endDate: string) => {
    console.log('Date range changed:', startDate, 'to', endDate);
    fetchReports(startDate, endDate);
  }, [fetchReports]);

  // Handle class change
  const handleClassChange = useCallback((className: string) => {
    setSelectedClass(className);
    setSelectedClassSectionId(null);
    // No API call needed - filtering will be done locally
  }, []);

  // Handle section change
  const handleSectionClick = useCallback((classSectionId: number | null) => {
    if (classSectionId === selectedClassSectionId) return;

    setSelectedClassSectionId(classSectionId);
    // No API call needed - filtering will be done locally
  }, [selectedClassSectionId]);

  // Export functions
  const handleExportExcel = async () => {
    if (!filteredReports || filteredReports.length === 0) {
      setError('No data to export');
      setShowErrorModal(true);
      return;
    }

    try {
      const exportColumns = [
        { title: 'Class', dataIndex: 'className' },
        { title: 'Section', dataIndex: 'sectionName' },
        { title: 'Date', dataIndex: 'summaryKey' },
        { title: 'Male', dataIndex: 'male' },
        { title: 'Female', dataIndex: 'female' },
        { title: 'Total Students', dataIndex: 'total' },
        { title: 'Present', dataIndex: 'studentsPresent' },
        { title: 'Absent', dataIndex: 'studentsAbsent' },
        { title: 'On Duty', dataIndex: 'studentOdCount' },
        { title: 'Meal Served Count', dataIndex: 'mealsServedCount' },
        { title: 'Meal Not Served Count', dataIndex: 'melasNotServedCount' },
      ];

      const fileName = `meal-report-${ new Date().toISOString().split('T')[0] }.xlsx`;
      exportToExcel(filteredReports, exportColumns, fileName);

      setSuccessMessage('Report exported to Excel successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export report');
      setShowErrorModal(true);
    }
  };

  const handleExportPDF = async () => {
    if (!filteredReports || filteredReports.length === 0) {
      setError('No data to export');
      setShowErrorModal(true);
      return;
    }

    try {
      const exportColumns = [
        { title: 'Class', dataIndex: 'className' },
        { title: 'Section', dataIndex: 'sectionName' },
        { title: 'Date', dataIndex: 'summaryKey' },
        { title: 'Male', dataIndex: 'male' },
        { title: 'Female', dataIndex: 'female' },
        { title: 'Total Students', dataIndex: 'total' },
        { title: 'Present', dataIndex: 'studentsPresent' },
        { title: 'Absent', dataIndex: 'studentsAbsent' },
        { title: 'On Duty', dataIndex: 'studentOdCount' },
        { title: 'Meal Served Count', dataIndex: 'mealsServedCount' },
        { title: 'Meal Not Served Count', dataIndex: 'melasNotServedCount' },
      ];

      const fileName = `meal-report-${ new Date().toISOString().split('T')[0] }.pdf`;
      exportToPDF(filteredReports, exportColumns, fileName);

      setSuccessMessage('Report exported to PDF successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export report');
      setShowErrorModal(true);
    }
  };

  // Initialize data based on user role
  useEffect(() => {
    // Fetch all reports initially - filtering will be done locally
    const today = new Date().toISOString().split('T')[0];
    if (fetchReportsRef.current) {
      fetchReportsRef.current(today, today);
    }
  }, []); // Empty dependency array - run only once on mount


  const columns = [
    {
      title: "Class",
      dataIndex: "className",
      sorter: (a: TableData, b: TableData) => (a?.className || '').localeCompare(b?.className || ''),
    },
    {
      title: "Section",
      dataIndex: "sectionName",
      sorter: (a: TableData, b: TableData) => (a?.sectionName || '').localeCompare(b?.sectionName || ''),
    },
    {
      title: "Date",
      dataIndex: "summaryKey",
      sorter: (a: TableData, b: TableData) => (a?.summaryKey || '').localeCompare(b?.summaryKey || ''),
    },
    {
      title: "Male",
      dataIndex: "male",
      sorter: (a: TableData, b: TableData) => ((a as any)?.male || 0) - ((b as any)?.male || 0),
    },
    {
      title: "Female",
      dataIndex: "female",
      sorter: (a: TableData, b: TableData) => ((a as any)?.female || 0) - ((b as any)?.female || 0),
    },
    {
      title: "Total Students",
      dataIndex: "total",
      sorter: (a: TableData, b: TableData) => ((a as any)?.total || 0) - ((b as any)?.total || 0),
    },
    {
      title: "Present",
      dataIndex: "studentsPresent",
      sorter: (a: TableData, b: TableData) => ((a as any)?.studentsPresent || 0) - ((b as any)?.studentsPresent || 0),
    },
    {
      title: "Absent",
      dataIndex: "studentsAbsent",
      sorter: (a: TableData, b: TableData) => ((a as any)?.studentsAbsent || 0) - ((b as any)?.studentsAbsent || 0),
    },
    {
      title: "On Duty",
      dataIndex: "studentOdCount",
      sorter: (a: TableData, b: TableData) => ((a as any)?.studentOdCount || 0) - ((b as any)?.studentOdCount || 0),
    },
    {
      title: "Meal Served Count",
      dataIndex: "mealsServedCount",
      sorter: (a: TableData, b: TableData) => ((a as any)?.mealsServedCount || 0) - ((b as any)?.mealsServedCount || 0),
    },
    {
      title: "Meal Not Served Count",
      dataIndex: "melasNotServedCount",
      sorter: (a: TableData, b: TableData) => ((a as any)?.melasNotServedCount || 0) - ((b as any)?.melasNotServedCount || 0),
    },

  ];

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Report</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={dashboardLink}>Dashboard</Link>
                      
                    </li>
                    {/* <li className="breadcrumb-item">
                      <Link to="#">Report</Link>
                    </li> */}
                    <li className="breadcrumb-item active" aria-current="page">
                      Meal Report
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                {/* Loading indicator for reports */}
                {isLoadingReports && (
                  <div className="me-3 d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <small className="text-muted">Loading reports...</small>
                  </div>
                )}

                {/* Export buttons */}
                <TooltipOption
                  onExportPDF={handleExportPDF}
                  onExportExcel={handleExportExcel}
                />
              </div>
            </div>

            {/* Role indicator for Headmaster */}
            {userRole === 'Headmaster' && (
              <div className="row mb-3">
                <div className="col-12">
                  <div className="alert alert-info">
                    <i className="ti ti-info-circle me-2"></i>
                    <strong>Headmaster View:</strong> Showing all meal reports from your school
                  </div>
                </div>
              </div>
            )}
            {/* Teacher Attendence List */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-3">
                  Reports
                  {userRole === 'Headmaster' && (
                    <small className="text-muted ms-2">
                      (all sections)
                    </small>
                  )}
                  {userRole === 'Teacher' && (
                    <small className="text-muted ms-2">
                      {selectedClass && selectedClassSectionId ? (
                        `(${ selectedClass } - ${ sectionsForSelectedClass.find((s: any) => s.classSectionId === selectedClassSectionId)?.sectionName })`
                      ) : selectedClass ? (
                        `(${ selectedClass } - all sections)`
                      ) : (
                        '(all classes and sections)'
                      )}
                    </small>
                  )}
                </h4>
                <div className="d-flex align-items-center flex-wrap">
                  <div className="input-icon-start mb-3 me-2 position-relative">
                    <PredefinedDateRanges onDateChange={handleDateRangeChange} />
                  </div>
                  <div className="dropdown mb-3 me-2">
                    <Link
                      to="#"
                      className="btn btn-outline-light bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                      style={{ opacity: isLoadingReports ? 0.6 : 1 }}
                    >
                      <i className="ti ti-filter me-2" />
                      {isLoadingReports ? 'Loading...' : 'Filter'}
                    </Link>
                    <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                      <form >
                        <div className="d-flex align-items-center border-bottom p-3">
                          <h4>Filter</h4>
                        </div>
                        <div className="p-3 border-bottom">
                          <div className="row">
                            {/* Class Selection - Only for Teachers */}
                            {userRole === 'Teacher' && (
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Class</label>
                                  <div className="position-relative">
                                    <CommonSelect
                                      options={classOptions}
                                      defaultValue={currentOption}
                                      onChange={(opt) => handleClassChange(opt?.value ?? "")}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Section Selection - Only for Teachers */}
                            {userRole === 'Teacher' && selectedClass && (
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Section</label>
                                  <div className="position-relative">
                                    <CommonSelect
                                      key={selectedClass ?? "no-class"}
                                      options={sectionOptions}
                                      defaultValue={currentSectionOption}
                                      isDisabled={sectionOptions.length === 0}
                                      onChange={(opt) => {
                                        const id = opt?.value ? parseInt(opt.value, 10) : null;
                                        handleSectionClick(id);
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="row mt-2">
                            <div className="col-md-6">
                              <div className="mb-0">
                                <label className="form-label">Attendance</label>
                                <div className="position-relative">
                                  <CommonSelect
                                    options={attendanceOptions}
                                    defaultValue={currentAttendanceOption}
                                    onChange={(opt) => setSelectedAttendance(opt?.value ?? null)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="p-3 d-flex align-items-center justify-content-end">
                          <button
                            type="button"
                            className="btn btn-light me-3"
                            onClick={() => {
                              // Reset all filters
                              setSelectedClass(null);
                              setSelectedClassSectionId(null);
                              setSelectedAttendance(null);
                              // No API call needed - filtering will be done locally
                            }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleApplyClick}
                          >
                            Apply
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body p-0 py-3">
                {/* Loading State */}
                {isLoadingReports && (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-3">Loading reports...</span>
                  </div>
                )}

                {/* Student List */}
                {!isLoadingReports && (
                  <Table dataSource={filteredReports} columns={columns} Selection={true} isShowSearch={false} />
                )}
                {/* /Student List */}
              </div>
            </div>
          </div>
          {/* /Teacher Attendence List */}
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </>
  );
};

export default MealReport;